import type {
  BannerName,
  ButtonName,
  CheckboxName,
  FilterSource,
  MessageTemplateType,
  ModalName,
} from '@coursera/event-pulse-types';

import type { AIMessageActionKey } from 'bundles/enterprise-admin-messaging-v2/utils/aiMessageActions';
import type { CourseraTemplateId } from 'bundles/enterprise-admin-messaging-v2/utils/courseraTemplates';

/**
 * Analytics taxonomy for the Messaging Revamp. Each constant below holds the
 * exact members of the matching `@coursera/event-pulse-types` enum (`ModalName`,
 * `ButtonName`, `BannerName`, `CheckboxName`) and is validated with `satisfies`
 * so a value that drifts from the SDK fails to compile. The SDK schema is the
 * source of truth; these constants only give the bundle readable, DRY names.
 *
 * `textInput.name`, `radio.id`, and `radio.value` are free-form `string` in the
 * SDK schema, so those constants carry no enum constraint.
 *
 * Members defined in the SDK enums but intentionally unused here (the UI does
 * not exist in this bundle yet): the `messaging_coursera_templates` /
 * `messaging_edit_message` modals; the `enterprise_admin_messaging_preview_template`
 * / `_save_changes` / `_add_tag` / `_set_audience` / `_remind_learners` /
 * `_templates` / `_send` buttons; and the AI-failure / send-failed banners
 * (`messaging_ai_*`, `messaging_send_failed`).
 */

export const MESSAGING_MODAL = {
  CREATE_STEP1: 'messaging_create_step1',
  CREATE_STEP2: 'messaging_create_step2',
  MANUAL_COMPOSE: 'messaging_manual_compose',
  SEND_SUCCESS: 'messaging_send_success',
  PREVIEW_EMAIL: 'messaging_preview_email',
  LEARNER_LIST: 'messaging_learner_list',
  EDIT_LEARNERS: 'messaging_edit_learners',
  CONFIRM_DISCARD: 'messaging_confirm_discard',
  MESSAGE_DETAILS: 'messaging_message_details',
  EDIT_AND_RESEND: 'messaging_edit_and_resend',
  CONFIRM_PAUSE: 'messaging_confirm_pause',
  COURSERA_TEMPLATES: 'messaging_coursera_templates',
} as const satisfies Record<string, ModalName>;

export const MESSAGING_BUTTON = {
  CREATE_MESSAGE: 'enterprise_admin_create_message',
  MESSAGE_DETAILS: 'enterprise_admin_messaging_message_details',
  EDIT_AND_RESEND: 'enterprise_admin_messaging_edit_and_resend',
  PAUSE_AUTO_SEND: 'enterprise_admin_messaging_pause_auto_send',
  GENERATE: 'enterprise_admin_messaging_generate',
  CREATE_MANUALLY: 'enterprise_admin_create_manually',
  USE_AI_PROMPT: 'enterprise_admin_use_ai_prompt',
  VIEW_LEARNER_LIST: 'enterprise_admin_messaging_view_learner_list',
  PREVIEW_MESSAGE: 'enterprise_admin_messaging_preview_message',
  CONFIRM_SEND: 'enterprise_admin_messaging_confirm_send',
  RESET_TO_ORIGINAL_MESSAGE: 'enterprise_admin_messaging_reset_to_original_message',
  UPLOAD_CSV: 'enterprise_admin_messaging_upload_csv',
  DONE: 'enterprise_admin_messaging_done',
  EDIT_LEARNER_LIST: 'enterprise_admin_messaging_edit_learner_list',
  SAVE_LEARNERS: 'enterprise_admin_messaging_save_learners',
  RESET_LEARNER_LIST: 'enterprise_admin_messaging_reset_learner_list',
  DISCARD: 'enterprise_admin_messaging_discard',
  CONFIRM_PAUSE_AUTO_SEND: 'enterprise_admin_messaging_confirm_pause_auto_send',
  TEMPLATES: 'enterprise_admin_messaging_templates',
  PREVIEW_TEMPLATE: 'enterprise_admin_messaging_preview_template',
} as const satisfies Record<string, ButtonName>;

// `textInput.name` is free-form `string` in the SDK schema; these are just the
// bundle's stable field identifiers.
export const MESSAGING_TEXT_INPUT = {
  AI_PROMPT: 'enterprise_admin_messaging_ai_prompt',
  SUBJECT: 'enterprise_admin_messaging_subject',
  BODY: 'enterprise_admin_messaging_body',
  CTA_LABEL: 'enterprise_admin_messaging_cta_label',
  BUTTON_URL: 'enterprise_admin_messaging_button_url',
  LEARNER_SEARCH: 'enterprise_admin_messaging_learner_search',
} as const;

/** Union of this bundle's text-input names (derived from {@link MESSAGING_TEXT_INPUT}). */
export type MessagingTextInputName = (typeof MESSAGING_TEXT_INPUT)[keyof typeof MESSAGING_TEXT_INPUT];

// `radio.id` is free-form `string` in the SDK schema (tone + auto-send cadence).
export const MESSAGING_RADIO = {
  TONE_SELECTOR: 'messaging_tone_selector',
  AUTO_SEND_FREQUENCY: 'messaging_auto_send_frequency',
} as const;

export const MESSAGING_BANNER = {
  SEND_SUCCESS: 'messaging_send_success',
  AUTO_SEND_PAUSED: 'messaging_auto_send_paused',
  AUTO_SEND_OFF: 'messaging_auto_send_off',
  AI_GENERATION_FAILED: 'messaging_ai_generation_failed',
  EDIT_LEARNERS_WARNING: 'messaging_edit_learners_warning',
} as const satisfies Record<string, BannerName>;

export const MESSAGING_CHECKBOX = {
  AUTO_SEND_TOGGLE: 'enterprise_admin_messaging_auto_send_toggle',
} as const satisfies Record<string, CheckboxName>;

/**
 * `filterSource` carried on `apply_filter` from the sent-messages table. The SDK
 * `FilterSource` enum has no messaging-specific value; the filters are CDS
 * popover dropdowns, so `dropdown_filter` is the closest shared member.
 */
export const MESSAGING_FILTER_SOURCE: FilterSource = 'dropdown_filter';

/**
 * Maps an internal AI-prompt action key to the DS proposal's suggestion id.
 * The Define-goal suggestion chips are keyed by {@link AIMessageActionKey} in
 * code, but DS instruments them under a separate id scheme; this is the agreed
 * translation. (Capitalization is taken verbatim from the DS PRD list and is
 * intentionally inconsistent — pending DS confirmation.)
 */
export const SUGGESTION_ID_BY_ACTION_KEY: Record<AIMessageActionKey, string> = {
  REMIND_TO_JOIN: 'Remind_invited_to_join',
  MESSAGE_INACTIVE_LEARNERS: 'remind_inactive_15d',
  ENCOURAGE_NEW_ENROLLEES: 'encourage_enrollees_get_started',
  REMIND_TO_ENROLL: 'Remind_to_enroll',
  MESSAGE_ENROLLED_LEARNERS: 'encourage_continue_progress',
  SEND_ACCOLADES: 'congratulate_completion',
  REMINDER_TO_COMPLETE_COURSE: 'Remind_to_complete_course',
  COURSE_DEADLINE_REMINDER: 'notify_course_deadline',
  REENGAGE_LONG_INACTIVE: 'reengage_inactive_30d',
  PROMOTE_POPULAR_COURSES: 'Share_trending_courses',
  CAREER_OPPORTUNITIES: 'motivate_upskill',
  WELCOME_NEW_JOINERS: 'welcome_new_joiners',
  ENCOURAGE_ONGOING_LEARNING: 'Promote_continuous_learning',
  REMIND_UPSKILLING_BENEFITS: 'highlight_upskilling_value',
};

/**
 * Maps a Coursera template id to the `messageTemplate.type` carried on the
 * `click_button` preview_template event, so analytics can tell which template
 * was previewed (DS §5.4). The 7 ids map 1:1 to the SDK `MessageTemplateType`;
 * `satisfies Record<CourseraTemplateId, …>` keeps this exhaustive — adding a
 * template without a type mapping fails to compile.
 */
export const MESSAGE_TEMPLATE_TYPE_BY_ID = {
  'assigned-content-reminder': 'reminder_assigned_content',
  'join-program-reminder': 'reminder_join_program',
  'enroll-course-reminder': 'reminder_enroll',
  'course-completion-congrats': 'congratulate_completion',
  'inactive-learner-reminder': 'reminder_inactive',
  'start-enrolled-course-reminder': 'reminder_start_course',
  'continue-course-reminder': 'reminder_continue_course',
} as const satisfies Record<CourseraTemplateId, MessageTemplateType>;
